import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { notify, usPhoneFormat, isMobileDevice } from "../../helpers";
import { signUp } from "../../services/signUp";
import { useDocumentTitle } from "../../hooks/customDocumentTitleHook";
import classNames from "classnames";
import Button from "../UI/Button";
import Spacing from "../Spacing";
import { Config } from "../../Config";
import { Eye, EyeOff, Lock } from "lucide-react";
import { getSpecialities } from "../../services/users";
import Inputmask from "inputmask";
import Select from "react-select";

function validatePassword(pwd) {
  if (!pwd) return { error: null, valid: false };
  const rules = [
    { valid: /[a-z]/.test(pwd),        msg: "→ must include a lowercase letter." },
    { valid: /[A-Z]/.test(pwd),        msg: "→ must include an uppercase letter." },
    { valid: /[0-9]/.test(pwd),        msg: "→ must include a number." },
    { valid: /[^A-Za-z0-9]/.test(pwd), msg: "→ must include a special character." },
    { valid: pwd.length >= 12,         msg: "→ must be at least 12 characters." },
  ];
  const firstError = rules.find(r => !r.valid)?.msg || null;
  return { error: firstError, valid: !firstError };
}

export default function SignUp() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get("token");
  // const signupSuccess = searchParams.get("success");
  

  const [validToken, setValidToken] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(searchParams.get("success"));
  const [loading, setLoading] = useState(false);
  const [isContactValid, setIsContactValid] = useState(true);

  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [disableSignUp, setDisableSignUp] = useState(true);

  const [allSpeciality, setAllSpeciality] = useState([]);
  const [deviceType, setDeviceType] = useState('');
  useEffect(() => {
    const UserDeviceType = isMobileDevice() ? 'mobile' : 'desktop';
    setDeviceType(UserDeviceType);
  }, []);

  const [formData, setFormData] = useState({
    username: "",
    first_name: "",
    last_name: "",
    contact: "",
    password: "",
    confirm_password: "",
    speciality_ids: [32],
    is_provider: true,
  });

  const navigate = useNavigate();
  const now = new Date();
  const currentYear = now.getFullYear();

  useEffect(() => {
    if (token) {
      try {
        const decodedToken = atob(token);
        const parsedToken = JSON.parse(decodedToken);
        const expiryDate = new Date(parsedToken.exp_timestamp);
        const nowUtc = new Date(new Date().toISOString());
        if (nowUtc > expiryDate) {
          setValidToken(false);
          notify("error", "The invitation token has expired. Please contact the sender to receive a new link.", 5);
        } else {
          // setRoles(parsedToken.roles || []);
          setValidToken(true);
        }
      } catch (error) {
        setValidToken(false);
        console.error("Invalid token:", error);
        notify("error", "The invitation token you're using is invalid. Contact the sender to get a valid link.", 5);
      }
    }
  }, [token]);

  const getAllSpeciality = async () => {
    try {
      const response = await getSpecialities();
      if (response?.data?.length) {
        const allSpecialities = response?.data?.map((option) => ({
          value: option?.id,
          label: option?.name,
        }))
        setAllSpeciality(allSpecialities);
      };
    } catch (error) {
      // console.log("🚀 ~ getAllSpeciality: ~ error:", error);
    }
  };
  useEffect(() => {
    getAllSpeciality();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "contact") {      
      const phoneInputs = document.querySelectorAll(".us_phone");
      Inputmask({
        mask: "(999) 999-9999",
        showMaskOnHover: false,
        showMaskOnFocus: true,
      }).mask(phoneInputs);
      let isContactValid = [0, 10].includes(value.replace(/\D/g, '').length);
      setIsContactValid(isContactValid);
    }
    setFormData(d => ({ ...d, [name]: value }));
  };

  const handleSpecialityChange = (selectedOption) => {
    const selectedValues = selectedOption ? selectedOption.map((option) => option.value) : [];
    if (selectedValues.length > 0) {
      setFormData({
        ...formData,
        speciality_ids: selectedValues,
        is_provider: true,
      });
    } else {
      setFormData({
        ...formData,
        speciality_ids: selectedValues,
        is_provider: false,
      });
    }
  };

  const checkAllRequiredFields = (show_tost = false) => {
    if (formData.first_name.length === 0) {
      if (show_tost) notify("error", "First name is required", 3);
      setDisableSignUp(true);
      return;
    }
    
    if (formData.last_name.length === 0) {
      if (show_tost) notify("error", "Last name is required", 3);
      setDisableSignUp(true);
      return;
    }        
    if (!isContactValid) {
      if (show_tost) notify("error", "Please enter a valid contact number.", 5);
      setDisableSignUp(true);
      return;
    }
    if (formData.username.length < 6) {
      if (show_tost) notify("error", "Username must be at least 6 characters", 3);
      setDisableSignUp(true);
      return;
    }
    if (formData.password.length === 0) {
      if (show_tost) notify("error", "Password is required", 3);
      setDisableSignUp(true);
      return;
    }
    if (formData.confirm_password.length === 0) {
      if (show_tost) notify("error", "Confirm password is required", 3);
      setDisableSignUp(true);
      return;
    }
    if (formData.confirm_password !== formData.password) {
      if (show_tost) notify("error", "Passwords do not match", 3);
      setDisableSignUp(true);
      return;
    }
    if (!newPwValid) {
      if (show_tost) notify("error", "Password must be at least 12 characters", 3);
      setDisableSignUp(true);
      return;
    }
    if (confirmError) {
      if (show_tost) notify("error", confirmError, 3);
      setDisableSignUp(true);
      return;
    }
    setDisableSignUp(false);
    return true;    
  };
  useEffect(() => {
    checkAllRequiredFields(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  // Password validations
  const { error: newPwError, valid: newPwValid } = useMemo(
    () => validatePassword(formData.password),
    [formData.password]
  );

  const confirmError = useMemo(() => {
    if (!formData.confirm_password) return null;
    if (formData.confirm_password !== formData.password) return "Passwords do not match.";
    return null;
  }, [formData.confirm_password, formData.password]);

  // Border classes
  const newPwBorderClass =
    formData.password.length === 0
      ? "" // neutral
      : newPwError
      ? "docpro_border_red"
      : "docpro_border_green";

  const confirmBorderClass =
    formData.confirm_password.length === 0
      ? "" // neutral
      : confirmError
      ? "docpro_border_red"
      : (formData.confirm_password === formData.password && newPwValid)
      ? "docpro_border_green"
      : ""; // base password invalid: keep neutral

  const handleSignUp = async (e) => {
    e.preventDefault();
    if (!token) {
      notify("error","You are not authorized to sign up. Please ensure you have received an invitation token and try again.",5);
      return;
    } 
    if (!checkAllRequiredFields(true)) return;
    setLoading(true);
    
    try {
      const response = await signUp(formData, token);
      if(response?.data?.status === "success"){
        notify("success", response?.data?.message, 3);
        if(deviceType !== "mobile"){
          await new Promise(resolve => setTimeout(resolve, 2000));
          let login_url = "/login";
          try {
            const rawString = `${formData.username},${formData.password}`;
            login_url = `/login?auth=${btoa(rawString)}`;
          } catch (error) {
            console.error("Encode failed:", error);
          }
          navigate(login_url);
        }
        const newUrl = window.location.href.replace("token=", "success=");
        window.location.replace(newUrl);
        setSignupSuccess("true");
      } else {
        notify("error", response?.data?.message, 5);
        setSignupSuccess("");
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.errorMessage ||
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        "Oops! Server throws error";
      notify("error", errorMessage, 5);
    } finally {
      setLoading(false);
    }
  };

  useDocumentTitle("DocPro - Create Your Account");

  return (
    <>
      {validToken ? (
        <div className="docpro_login_wrap">
          <div className="docpro_login_thumb" style={{backgroundImage: "url(/images/login_img.png)" }} />
          {/* <div className="docpro_login_thumb" style={{boxShadow: "inset -10px -3px 0px -8px #ffffff", backgroundImage: "linear-gradient(to right, rgba(255,255,255,0) 80%, rgba(255,255,255,1) 100%), url(/images/login_img.png)" }} /> */}
          
          
          <div className="docpro_login_right" style={{ backgroundColor: deviceType === "mobile" ? "#f2f2f7" : "" }}> 
            <div className="docpro_login_card_wrap">
              <div className="docpro_login_card">
                <div className="docpro_text_center" style={{ padding: "0 10px" }}>
                  {deviceType !== "mobile" && (
                    <img src={`${Config.appUrl}/applogo.png`} alt="Docpro Logo" style={{ width: "50px" }} />
                  )}
                  
                  <Spacing lg="20" sm="20" />
                  <h1 className="docpro_mb_0" style={{ fontSize: "26px" }}>Set up your docpro account.</h1>
                  <Spacing lg="10" sm="10" />
                  <p style={{ fontSize: "18px", padding: "10px" }}>We sync patient schedules and push notes to EHR with this connection.</p>
                </div>

                

                <label className="docpro_fw_500 docpro_primary_color docpro_fs_16">
                  First Name <span style={{ color: "red" }}>*</span>
                </label>
                <input
                  type="text"
                  className={classNames("docpro_form_field size_lg")}
                  value={formData.first_name}
                  name="first_name"
                  onChange={(e) => { handleChange(e) }}
                />

                <Spacing lg="20" sm="20" />
                <label className="docpro_fw_500 docpro_primary_color docpro_fs_16">
                  Last Name <span style={{ color: "red" }}>*</span>
                </label>
                <input
                  type="text"
                  className={classNames("docpro_form_field size_lg")}
                  value={formData.last_name}
                  name="last_name"
                  onChange={(e) => { handleChange(e) }}
                />

                <Spacing lg="20" sm="20" />
                <div className="docpro_grid_item">
                  <label className="docpro_fw_500 docpro_primary_color docpro_fs_16">Contact</label>
                  <input
                    type="text"
                    // className="docpro_form_field size_lg us_phone"
                    className={classNames("docpro_form_field size_lg us_phone", {
                      danger_alert: !isContactValid,
                    })}
                    name="contact"
                    autoComplete="off"
                    onChange={(e) => { handleChange(e) }}
                    defaultValue={formData.contact ? usPhoneFormat(formData.contact) : ""}
                    maxLength={20}
                    placeholder="(123) 123 1234"
                  />
                </div>

                <Spacing lg="20" sm="20" />
                {allSpeciality?.length > 0 && (
                    <div className="docpro_grid_item">
                      <label className="docpro_fw_500 docpro_primary_color docpro_fs_16">
                        Specialities
                      </label>
                      <Select
                        isMulti
                        name="speciality_ids"
                        options={allSpeciality}
                        placeholder="Search..."
                        isSearchable
                        value={allSpeciality?.filter((option) => 
                          formData.speciality_ids?.includes(option.value)
                        ) || []}
                        onChange={(selectedOption) => handleSpecialityChange(selectedOption)}
                        className="react-select-container w-full"
                        classNamePrefix="react-select"
                        filterOption={(option, rawInput) => {
                          const words = rawInput?.toLowerCase()?.split(" ");
                          return words?.every((word) =>
                            option?.label?.toLowerCase()?.includes(word)
                          );
                        }}
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: '40px',
                            borderColor: 'var(--border)',
                            borderRadius: '8px'
                          }),
                          menu: (base) => ({
                            ...base,
                            zIndex: 1050,
                            marginTop: '-2px',
                            borderRadius: '0 0 3px 3px',
                          }),
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 1050,
                            padding: 0
                          }),
                        }}
                      />
                    </div>
                  )}
                <Spacing lg="20" sm="20" />
                <label className="docpro_fw_500 docpro_primary_color docpro_fs_16">
                  Username <span style={{ color: "red" }}>*</span>
                  <small style={{ color: "green" }}>(This will be your login username)</small>
                </label>
                <input
                  type="text"
                  className={classNames("docpro_form_field size_lg", {
                    danger_alert: formData.username.length > 0 && formData.username.length < 6,
                  })}
                  value={formData.username}
                  name="username"
                  onChange={(e) => { handleChange(e) }}
                />

                {/* NEW PASSWORD */}
                <Spacing lg="20" sm="20" />
                <div className="docpro_grid_item">
                  <label className="docpro_fw_500 docpro_primary_color docpro_fs_16">
                    Set Password <span style={{ color: "red" }}>*</span>
                  </label>
                  <div className="docpro_form_field_with_icon">
                    <input
                      name="password"
                      type={showNew ? "text" : "password"}
                      placeholder="••••••••••••"
                      className={classNames("docpro_form_field size_lg", newPwBorderClass)}
                      onChange={(e) => { handleChange(e) }}
                      autoComplete="new-password"
                    />
                    <Lock width={18} height={18} className="docpro_form_field_icon" />
                    {showNew ? (
                      <Eye onClick={() => setShowNew(false)} width={18} height={18} className="docpro_form_field_icon pointer" style={{ color: "#b0b0b0" }} />
                    ) : (
                      <EyeOff onClick={() => setShowNew(true)} width={18} height={18} className="docpro_form_field_icon pointer" style={{ color: "#b0b0b0" }} />
                    )}
                  </div>
                  {/* helper line (new password) */}
                  <div className="docpro_pw_help" aria-live="polite">
                    {formData.password.length === 0 ? null : newPwError ? (
                      <span className="docpro_pw_error">{newPwError}</span>
                    ) : null}
                  </div>
                </div>

                {/* CONFIRM PASSWORD */}
                <Spacing lg="20" sm="20" />
                <div className="docpro_grid_item">
                  <label className="docpro_fw_500 docpro_primary_color docpro_fs_16">
                    Confirm Password <span style={{ color: "red" }}>*</span>
                  </label>
                  <div className="docpro_form_field_with_icon">
                    <input
                      name="confirm_password"
                      type={showConfirm ? "text" : "password"}
                      placeholder="••••••••••••"
                      className={classNames("docpro_form_field size_lg", confirmBorderClass)}
                      onChange={(e) => { handleChange(e) }}
                      autoComplete="new-password"
                    />
                    <Lock width={18} height={18} className="docpro_form_field_icon" />
                    {showConfirm ? (
                      <Eye onClick={() => setShowConfirm(false)} width={18} height={18} className="docpro_form_field_icon pointer" style={{ color: "#b0b0b0" }} />
                    ) : (
                      <EyeOff onClick={() => setShowConfirm(true)} width={18} height={18} className="docpro_form_field_icon pointer" style={{ color: "#b0b0b0" }} />
                    )}
                  </div>
                  {/* helper line (confirm) */}
                  <div className="docpro_pw_help" aria-live="polite">
                    {formData.confirm_password.length === 0 ? null : confirmError ? (
                      <span className="docpro_pw_error">{confirmError}</span>
                    ) : null}
                  </div>
                </div>

                <Spacing lg="30" sm="30" />
                <Button filled lg loading={loading} onClick={handleSignUp} classes={"width_100"} disabled={disableSignUp}>
                  Set up account
                </Button>

                <Spacing lg="40" sm="40" />
              </div>
            </div>

            <footer className="docpro_login_footer">
              <div className="docpro_login_footer_nav">
                <Link to="/">Privacy policy</Link>
                <Link to="/">Terms & conditions</Link>
                <Link to="/">Terms of service</Link>
                <Link to="/">HIPAA Compliance</Link>
              </div>
              <p>© {currentYear} DocPro. All rights reserved.</p>
            <Spacing lg="10" sm="10" />
            </footer>
          </div>
        </div>
      ) : (
        <div className="docpro_login_wrap">
          <div className="docpro_login_thumb" style={{ backgroundImage: "url(/images/login_img.png)" }}></div>
          <div className="docpro_login_right">
            <div className="docpro_login_card_wrap">
              <div className="docpro_login_card">
                <div className="docpro_text_center">
                {signupSuccess ? (
                    <>
                      <h1 className="docpro_mb_0">You're all set.</h1>
                      <Spacing lg="10" sm="10" />
                      <p>You can now download DocPro app and start using it.</p>                      
                        <svg title="Download on App Store" xmlns="http://www.w3.org/2000/svg" width="235" height="79" viewBox="0 0 235 79" fill="none" onClick={() => window.open("https://apps.apple.com/us/app/docpro-ai-medical-scribe/id6746182167", "_self")}>
                          <g clipPath="url(#clip0_3621_9412)">
                            <path d="M216.286 0.000255298H18.7245C18.0044 0.000255298 17.2929 0.000255298 16.5746 0.00418296C15.9734 0.00811062 15.377 0.0195205 14.77 0.0291236C13.4513 0.0446414 12.1357 0.160654 10.8347 0.376152C9.53543 0.596342 8.2769 1.01146 7.10164 1.60747C5.92782 2.20853 4.85527 2.98953 3.92289 3.92216C2.98562 4.85217 2.20433 5.92713 1.60906 7.10571C1.01216 8.28194 0.598262 9.54239 0.381671 10.8435C0.163019 12.143 0.0453613 13.4574 0.029752 14.7751C0.0115277 15.3772 0.00960313 15.9813 0 16.5835V61.976C0.00960313 62.5858 0.0115277 63.1765 0.029752 63.7865C0.0453661 65.104 0.163024 66.4184 0.381671 67.7178C0.597664 69.0197 1.01159 70.2809 1.60906 71.4576C2.20406 72.6323 2.98546 73.703 3.92289 74.6278C4.85173 75.5646 5.92498 76.3461 7.10164 76.9426C8.2769 77.5402 9.53528 77.9578 10.8347 78.1813C12.1359 78.3951 13.4514 78.5112 14.77 78.5285C15.377 78.5419 15.9734 78.5496 16.5746 78.5496C17.2928 78.5535 18.0044 78.5535 18.7245 78.5535H216.286C216.992 78.5535 217.709 78.5535 218.415 78.5496C219.013 78.5496 219.627 78.5419 220.225 78.5285C221.541 78.5121 222.854 78.396 224.153 78.1813C225.457 77.9562 226.719 77.5387 227.9 76.9426C229.076 76.3458 230.148 75.5642 231.076 74.6278C232.011 73.6993 232.794 72.6295 233.397 71.4576C233.99 70.28 234.4 69.019 234.613 67.7178C234.832 66.4183 234.953 65.1042 234.977 63.7865C234.985 63.1765 234.985 62.5858 234.985 61.976C235 61.2625 235 60.553 235 59.828V18.7276C235 18.0084 235 17.295 234.985 16.5835C234.985 15.9813 234.985 15.3772 234.977 14.775C234.953 13.4573 234.832 12.1431 234.613 10.8434C234.399 9.54306 233.989 8.28272 233.397 7.10563C232.185 4.74329 230.262 2.82037 227.9 1.6073C226.719 1.01274 225.456 0.597732 224.153 0.375975C222.855 0.159527 221.542 0.0434748 220.225 0.0288487C219.627 0.0192652 219.013 0.00775713 218.415 0.00392766C217.709 0 216.992 0.000255298 216.286 0.000255298Z" fill="#A6A6A6"/>
                            <path d="M16.5841 76.8353C15.9858 76.8353 15.4019 76.8276 14.8083 76.8143C13.5785 76.7982 12.3516 76.6912 11.1376 76.494C10.0056 76.299 8.90903 75.9364 7.88402 75.418C6.86841 74.9039 5.94209 74.2298 5.14055 73.4216C4.32741 72.6228 3.65043 71.6964 3.13644 70.6791C2.61679 69.6551 2.25716 68.5575 2.07008 67.4246C1.86805 66.2073 1.75875 64.9763 1.74311 63.7424C1.73066 63.3282 1.71436 61.9492 1.71436 61.9492V16.5837C1.71436 16.5837 1.73172 15.2258 1.7432 14.8269C1.75818 13.595 1.86685 12.366 2.06828 11.1505C2.2557 10.0145 2.61561 8.91373 3.13554 7.88642C3.64764 6.86977 4.32086 5.94268 5.1291 5.14108C5.93644 4.33187 6.86572 3.65431 7.88306 3.13312C8.90572 2.6164 10.0002 2.25631 11.1299 2.06491C12.3479 1.86572 13.579 1.75802 14.8131 1.74273L16.5851 1.71875H218.405L220.198 1.74369C221.421 1.75822 222.641 1.86496 223.848 2.06299C224.989 2.25679 226.095 2.61939 227.129 3.13887C229.167 4.18922 230.826 5.85097 231.872 7.89121C232.384 8.91143 232.738 10.0032 232.923 11.1294C233.127 12.3548 233.241 13.5935 233.264 14.8356C233.27 15.3917 233.27 15.9891 233.27 16.5837C233.285 17.3201 233.285 18.021 233.285 18.7277V59.8281C233.285 60.5416 233.285 61.2378 233.27 61.9396C233.27 62.5783 233.27 63.1633 233.262 63.7654C233.239 64.9853 233.127 66.2019 232.927 67.4054C232.743 68.5465 232.386 69.6526 231.866 70.685C231.349 71.6913 230.676 72.6096 229.872 73.4062C229.069 74.2188 228.142 74.8969 227.124 75.4143C226.092 75.9366 224.988 76.3006 223.848 76.494C222.634 76.6922 221.407 76.7993 220.177 76.8143C219.602 76.8276 219 76.8353 218.415 76.8353L216.286 76.8392L16.5841 76.8353Z" fill="black"/>
                            <path d="M48.6419 39.864C48.6631 38.2248 49.0984 36.6177 49.9076 35.192C50.7167 33.7664 51.8734 32.5686 53.2698 31.71C52.3827 30.4431 51.2124 29.4004 49.8518 28.6648C48.4912 27.9293 46.9778 27.5211 45.4319 27.4727C42.1341 27.1266 38.9371 29.446 37.2566 29.446C35.5436 29.446 32.9563 27.5071 30.1701 27.5644C28.368 27.6226 26.6116 28.1467 25.0722 29.0855C23.5328 30.0243 22.2629 31.3459 21.3861 32.9215C17.588 39.4972 20.421 49.1613 24.0593 54.4766C25.8795 57.0794 28.0069 59.9868 30.7905 59.8837C33.5145 59.7707 34.5318 58.1467 37.8199 58.1467C41.0774 58.1467 42.0319 59.8837 44.872 59.8181C47.7949 59.7707 49.6365 57.2038 51.3929 54.5764C52.7008 52.7218 53.7072 50.6721 54.3748 48.5032C52.6767 47.785 51.2275 46.5828 50.2081 45.0465C49.1886 43.5102 48.6439 41.7078 48.6419 39.864Z" fill="white"/>
                            <path d="M43.2775 23.98C44.8712 22.0668 45.6564 19.6077 45.4663 17.125C43.0315 17.3807 40.7824 18.5444 39.1671 20.3842C38.3774 21.283 37.7725 22.3286 37.3871 23.4613C37.0018 24.5939 36.8434 25.7915 36.9211 26.9854C38.1389 26.9979 39.3437 26.734 40.4448 26.2134C41.5458 25.6928 42.5144 24.9292 43.2775 23.98Z" fill="white"/>
                            <path d="M83.0745 53.2954H73.7789L71.5466 59.8868H67.6094L76.414 35.5H80.5047L89.3093 59.8868H85.3049L83.0745 53.2954ZM74.7417 50.2537H82.11L78.4777 39.5562H78.376L74.7417 50.2537Z" fill="white"/>
                            <path d="M108.324 50.9943C108.324 56.5195 105.367 60.0694 100.904 60.0694C99.7739 60.1285 98.6496 59.8681 97.6603 59.3179C96.6709 58.7678 95.8565 57.9502 95.3102 56.9587H95.2258V65.7652H91.5762V42.1034H95.1088V45.0606H95.1759C95.7473 44.0739 96.5755 43.2605 97.5724 42.707C98.5693 42.1535 99.6976 41.8806 100.837 41.9173C105.35 41.9174 108.324 45.4845 108.324 50.9943ZM104.573 50.9943C104.573 47.3946 102.713 45.028 99.8746 45.028C97.0861 45.028 95.2105 47.4445 95.2105 50.9943C95.2105 54.5768 97.0861 56.976 99.8746 56.976C102.713 56.976 104.573 54.6266 104.573 50.9943Z" fill="white"/>
                            <path d="M127.893 50.9943C127.893 56.5195 124.936 60.0693 120.474 60.0693C119.343 60.1285 118.219 59.8681 117.229 59.3179C116.24 58.7678 115.426 57.9501 114.879 56.9586H114.795V65.7652H111.145V42.1034H114.678V45.0606H114.745C115.316 44.0739 116.145 43.2605 117.141 42.707C118.138 42.1535 119.267 41.8806 120.406 41.9173C124.919 41.9173 127.893 45.4844 127.893 50.9943ZM124.142 50.9943C124.142 47.3946 122.282 45.028 119.444 45.028C116.655 45.028 114.78 47.4444 114.78 50.9943C114.78 54.5768 116.655 56.9759 119.444 56.9759C122.282 56.9759 124.142 54.6266 124.142 50.9943Z" fill="white"/>
                            <path d="M140.827 53.0924C141.098 55.5108 143.447 57.0986 146.657 57.0986C149.734 57.0986 151.947 55.5107 151.947 53.3302C151.947 51.4373 150.612 50.3039 147.451 49.5272L144.291 48.7658C139.813 47.6841 137.734 45.5899 137.734 42.1915C137.734 37.9839 141.401 35.0938 146.608 35.0938C151.761 35.0938 155.293 37.9839 155.412 42.1915H151.728C151.507 39.7578 149.496 38.2888 146.556 38.2888C143.616 38.2888 141.604 39.7751 141.604 41.9384C141.604 43.6625 142.889 44.677 146.032 45.4537L148.719 46.1134C153.722 47.2967 155.801 49.3066 155.801 52.8736C155.801 57.4361 152.167 60.2936 146.387 60.2936C140.979 60.2936 137.327 57.5032 137.091 53.0923L140.827 53.0924Z" fill="white"/>
                            <path d="M163.678 37.8984V42.1061H167.059V44.9962H163.678V54.7981C163.678 56.3209 164.355 57.0304 165.841 57.0304C166.242 57.0234 166.643 56.9953 167.042 56.946V59.8189C166.373 59.9437 165.694 60.0003 165.015 59.9876C161.415 59.9876 160.011 58.6356 160.011 55.1874V44.9962H157.426V42.1061H160.011V37.8984H163.678Z" fill="white"/>
                            <path d="M169.017 51.0002C169.017 45.406 172.312 41.8906 177.45 41.8906C182.605 41.8906 185.884 45.4059 185.884 51.0002C185.884 56.6098 182.622 60.1098 177.45 60.1098C172.279 60.1098 169.017 56.6098 169.017 51.0002ZM182.166 51.0002C182.166 47.1627 180.407 44.8977 177.45 44.8977C174.492 44.8977 172.736 47.1799 172.736 51.0002C172.736 54.853 174.492 57.1007 177.45 57.1007C180.407 57.1007 182.166 54.853 182.166 51.0002Z" fill="white"/>
                            <path d="M188.893 42.111H192.374V45.1373H192.458C192.694 44.1921 193.248 43.3568 194.026 42.7718C194.805 42.1868 195.762 41.8878 196.735 41.925C197.156 41.9235 197.575 41.9692 197.985 42.0611V45.4748C197.454 45.3126 196.901 45.2381 196.346 45.2543C195.815 45.2328 195.287 45.3263 194.796 45.5283C194.306 45.7304 193.864 46.0362 193.503 46.4248C193.142 46.8134 192.869 47.2757 192.703 47.7797C192.537 48.2838 192.483 48.8177 192.543 49.3449V59.891H188.893L188.893 42.111Z" fill="white"/>
                            <path d="M214.812 54.667C214.321 57.8947 211.178 60.1098 207.156 60.1098C201.984 60.1098 198.774 56.6443 198.774 51.0846C198.774 45.5076 202.001 41.8906 207.003 41.8906C211.922 41.8906 215.016 45.2698 215.016 50.6607V51.9111H202.458V52.1317C202.4 52.786 202.482 53.4452 202.698 54.0655C202.914 54.6857 203.26 55.2529 203.713 55.7292C204.165 56.2055 204.714 56.5801 205.322 56.8282C205.93 57.0762 206.584 57.192 207.241 57.1678C208.103 57.2486 208.969 57.0489 209.709 56.5983C210.448 56.1477 211.023 55.4704 211.347 54.667L214.812 54.667ZM202.475 49.3605H211.364C211.397 48.7721 211.308 48.1833 211.102 47.6311C210.896 47.0788 210.579 46.575 210.169 46.1513C209.76 45.7276 209.267 45.3931 208.722 45.1688C208.177 44.9445 207.592 44.8352 207.003 44.8479C206.409 44.8443 205.82 44.9585 205.27 45.1839C204.72 45.4092 204.22 45.7412 203.799 46.1607C203.378 46.5803 203.044 47.079 202.817 47.6282C202.59 48.1774 202.474 48.7661 202.475 49.3605Z" fill="white"/>
                            <path d="M74.2843 17.1461C75.0495 17.0912 75.8173 17.2068 76.5323 17.4845C77.2474 17.7623 77.8919 18.1953 78.4193 18.7523C78.9468 19.3093 79.344 19.9764 79.5825 20.7055C79.8209 21.4346 79.8945 22.2075 79.798 22.9686C79.798 26.7121 77.7748 28.864 74.2843 28.864H70.0518V17.1461H74.2843ZM71.8717 27.2068H74.081C74.6278 27.2395 75.1749 27.1499 75.6827 26.9444C76.1904 26.739 76.6459 26.4229 77.0161 26.0192C77.3863 25.6155 77.6617 25.1343 77.8225 24.6107C77.9833 24.0872 78.0253 23.5343 77.9454 22.9924C78.0195 22.4527 77.9732 21.9033 77.81 21.3835C77.6468 20.8638 77.3706 20.3866 77.0013 19.9861C76.6319 19.5856 76.1786 19.2718 75.6737 19.067C75.1689 18.8623 74.625 18.7718 74.081 18.802H71.8717V27.2068Z" fill="white"/>
                            <path d="M81.854 24.4398C81.7984 23.8587 81.8648 23.2723 82.0491 22.7184C82.2334 22.1645 82.5314 21.6552 82.9241 21.2232C83.3167 20.7912 83.7954 20.446 84.3292 20.2098C84.8631 19.9736 85.4404 19.8516 86.0242 19.8516C86.608 19.8516 87.1854 19.9736 87.7192 20.2098C88.2531 20.446 88.7317 20.7912 89.1243 21.2232C89.517 21.6552 89.815 22.1645 89.9993 22.7184C90.1836 23.2723 90.2501 23.8587 90.1945 24.4398C90.2511 25.0215 90.1854 25.6087 90.0016 26.1635C89.8178 26.7184 89.52 27.2286 89.1272 27.6614C88.7345 28.0943 88.2555 28.4402 87.7211 28.6769C87.1867 28.9136 86.6087 29.0359 86.0242 29.0359C85.4397 29.0359 84.8617 28.9136 84.3273 28.6769C83.7929 28.4402 83.3139 28.0943 82.9212 27.6614C82.5284 27.2286 82.2306 26.7184 82.0468 26.1635C81.863 25.6087 81.7973 25.0215 81.854 24.4398ZM88.3994 24.4398C88.3994 22.523 87.5383 21.402 86.0271 21.402C84.5101 21.402 83.6568 22.523 83.6568 24.4398C83.6568 26.372 84.5102 27.4843 86.0271 27.4843C87.5383 27.4843 88.3994 26.3643 88.3994 24.4398Z" fill="white"/>
                            <path d="M101.281 28.8664H99.4708L97.6431 22.3536H97.505L95.685 28.8664H93.8919L91.4543 20.0234H93.2245L94.8086 26.7712H94.939L96.7571 20.0234H98.4313L100.249 26.7712H100.387L101.964 20.0234H103.709L101.281 28.8664Z" fill="white"/>
                            <path d="M105.759 20.0232H107.439V21.4279H107.57C107.791 20.9234 108.164 20.5004 108.637 20.218C109.11 19.9356 109.659 19.8078 110.209 19.8525C110.639 19.8201 111.071 19.885 111.473 20.0423C111.874 20.1996 112.236 20.4453 112.53 20.7611C112.824 21.077 113.043 21.455 113.171 21.867C113.299 22.2791 113.333 22.7146 113.269 23.1415V28.866H111.524V23.5797C111.524 22.1586 110.907 21.4519 109.616 21.4519C109.324 21.4383 109.032 21.488 108.761 21.5977C108.49 21.7073 108.246 21.8743 108.045 22.0871C107.845 22.3 107.693 22.5537 107.599 22.8308C107.506 23.108 107.474 23.4021 107.504 23.6929V28.8661H105.759L105.759 20.0232Z" fill="white"/>
                            <path d="M116.05 16.5703H117.795V28.8653H116.05V16.5703Z" fill="white"/>
                            <path d="M120.222 24.44C120.166 23.8588 120.233 23.2724 120.417 22.7185C120.601 22.1645 120.899 21.6552 121.292 21.2232C121.685 20.7912 122.163 20.446 122.697 20.2098C123.231 19.9736 123.809 19.8516 124.392 19.8516C124.976 19.8516 125.554 19.9736 126.087 20.2098C126.621 20.446 127.1 20.7912 127.493 21.2232C127.885 21.6552 128.183 22.1645 128.368 22.7185C128.552 23.2724 128.619 23.8588 128.563 24.44C128.62 25.0217 128.554 25.6089 128.37 26.1637C128.186 26.7185 127.888 27.2288 127.495 27.6616C127.103 28.0945 126.624 28.4403 126.089 28.677C125.555 28.9137 124.977 29.036 124.392 29.036C123.808 29.036 123.23 28.9137 122.695 28.677C122.161 28.4403 121.682 28.0945 121.289 27.6616C120.896 27.2288 120.598 26.7185 120.415 26.1637C120.231 25.6089 120.165 25.0217 120.222 24.44ZM126.767 24.44C126.767 22.5231 125.906 21.4022 124.395 21.4022C122.878 21.4022 122.024 22.5231 122.024 24.44C122.024 26.3722 122.878 27.4845 124.395 27.4845C125.906 27.4845 126.767 26.3645 126.767 24.44Z" fill="white"/>
                            <path d="M130.4 26.3644C130.4 24.7726 131.585 23.855 133.689 23.7246L136.085 23.5865V22.8232C136.085 21.8892 135.467 21.3618 134.274 21.3618C133.3 21.3618 132.625 21.7195 132.431 22.3447H130.742C130.92 20.8258 132.349 19.8516 134.355 19.8516C136.572 19.8516 137.822 20.9552 137.822 22.8232V28.8652H136.142V27.6225H136.004C135.724 28.0683 135.33 28.4317 134.863 28.6756C134.397 28.9195 133.874 29.0352 133.348 29.0109C132.977 29.0495 132.601 29.0099 132.246 28.8947C131.891 28.7794 131.565 28.591 131.287 28.3417C131.009 28.0923 130.787 27.7875 130.634 27.447C130.481 27.1064 130.402 26.7376 130.4 26.3644ZM136.085 25.6088V24.8695L133.925 25.0076C132.707 25.0891 132.155 25.5033 132.155 26.2829C132.155 27.0788 132.845 27.5419 133.795 27.5419C134.073 27.5701 134.354 27.542 134.621 27.4593C134.888 27.3767 135.136 27.2411 135.349 27.0608C135.563 26.8804 135.738 26.6589 135.865 26.4095C135.991 26.16 136.066 25.8877 136.085 25.6088Z" fill="white"/>
                            <path d="M140.116 24.44C140.116 21.6458 141.552 19.8757 143.786 19.8757C144.339 19.8502 144.888 19.9826 145.368 20.2573C145.848 20.532 146.24 20.9378 146.498 21.4271H146.629V16.5703H148.374V28.8653H146.701V27.4682H146.563C146.285 27.9542 145.88 28.3549 145.39 28.6269C144.901 28.8988 144.346 29.0317 143.786 29.0111C141.537 29.0112 140.116 27.241 140.116 24.44ZM141.919 24.44C141.919 26.3157 142.803 27.4443 144.281 27.4443C145.752 27.4443 146.661 26.2994 146.661 24.4477C146.661 22.6047 145.743 21.4435 144.281 21.4435C142.812 21.4435 141.919 22.5798 141.919 24.44Z" fill="white"/>
                            <path d="M155.594 24.4398C155.539 23.8587 155.605 23.2723 155.79 22.7184C155.974 22.1645 156.272 21.6552 156.665 21.2232C157.057 20.7912 157.536 20.446 158.07 20.2098C158.604 19.9736 159.181 19.8516 159.765 19.8516C160.348 19.8516 160.926 19.9736 161.46 20.2098C161.994 20.446 162.472 20.7912 162.865 21.2232C163.257 21.6552 163.555 22.1645 163.74 22.7184C163.924 23.2723 163.991 23.8587 163.935 24.4398C163.992 25.0215 163.926 25.6087 163.742 26.1635C163.558 26.7184 163.26 27.2286 162.868 27.6614C162.475 28.0943 161.996 28.4402 161.462 28.6769C160.927 28.9136 160.349 29.0359 159.765 29.0359C159.18 29.0359 158.602 28.9136 158.068 28.6769C157.533 28.4402 157.054 28.0943 156.662 27.6614C156.269 27.2286 155.971 26.7184 155.787 26.1635C155.603 25.6087 155.538 25.0215 155.594 24.4398ZM162.14 24.4398C162.14 22.523 161.279 21.402 159.768 21.402C158.251 21.402 157.397 22.523 157.397 24.4398C157.397 26.372 158.251 27.4843 159.768 27.4843C161.279 27.4843 162.14 26.3643 162.14 24.4398Z" fill="white"/>
                            <path d="M166.277 20.0232H167.957V21.4279H168.087C168.308 20.9234 168.681 20.5004 169.154 20.218C169.627 19.9356 170.177 19.8078 170.726 19.8525C171.156 19.8201 171.588 19.885 171.99 20.0423C172.392 20.1996 172.753 20.4453 173.047 20.7611C173.341 21.077 173.56 21.455 173.688 21.867C173.816 22.2791 173.85 22.7146 173.787 23.1415V28.866H172.042V23.5797C172.042 22.1586 171.424 21.4519 170.133 21.4519C169.841 21.4383 169.55 21.488 169.278 21.5977C169.007 21.7073 168.763 21.8743 168.563 22.0871C168.362 22.3 168.21 22.5537 168.117 22.8308C168.023 23.108 167.991 23.4021 168.022 23.6929V28.8661H166.277V20.0232Z" fill="white"/>
                            <path d="M183.648 17.8203V20.0622H185.564V21.5322H183.648V26.0793C183.648 27.0056 184.03 27.4112 184.898 27.4112C185.121 27.4105 185.343 27.3971 185.564 27.371V28.8247C185.251 28.8807 184.933 28.9106 184.615 28.9138C182.674 28.9138 181.901 28.2311 181.901 26.5262V21.5321H180.497V20.0621H181.901V17.8203H183.648Z" fill="white"/>
                            <path d="M187.948 16.5703H189.678V21.4435H189.816C190.048 20.9342 190.431 20.5088 190.914 20.2254C191.396 19.9419 191.954 19.8141 192.512 19.8594C192.94 19.8361 193.368 19.9075 193.765 20.0686C194.162 20.2298 194.519 20.4766 194.81 20.7914C195.101 21.1063 195.319 21.4815 195.448 21.8902C195.577 22.299 195.615 22.7312 195.557 23.1561V28.8654H193.811V23.5866C193.811 22.1741 193.153 21.4588 191.92 21.4588C191.62 21.4342 191.318 21.4754 191.036 21.5796C190.753 21.6838 190.497 21.8484 190.285 22.062C190.073 22.2755 189.91 22.5328 189.808 22.8159C189.706 23.0989 189.666 23.4009 189.693 23.7007V28.8653H187.948L187.948 16.5703Z" fill="white"/>
                            <path d="M205.733 26.4777C205.496 27.2859 204.983 27.985 204.282 28.4527C203.582 28.9204 202.739 29.1269 201.902 29.0361C201.319 29.0514 200.74 28.9398 200.205 28.709C199.67 28.4782 199.191 28.1337 198.803 27.6995C198.414 27.2652 198.124 26.7515 197.954 26.1942C197.784 25.6368 197.737 25.049 197.817 24.4717C197.739 23.8926 197.787 23.3036 197.957 22.7445C198.127 22.1855 198.415 21.6695 198.801 21.2314C199.188 20.7933 199.664 20.4434 200.198 20.2054C200.731 19.9673 201.31 19.8467 201.894 19.8517C204.355 19.8517 205.839 21.5327 205.839 24.3096V24.9186H199.595V25.0164C199.567 25.3409 199.608 25.6675 199.714 25.9754C199.821 26.2832 199.99 26.5654 200.212 26.8039C200.434 27.0424 200.703 27.232 201.002 27.3604C201.301 27.4889 201.624 27.5533 201.95 27.5497C202.367 27.5998 202.79 27.5247 203.165 27.3338C203.539 27.1429 203.849 26.8449 204.053 26.4777L205.733 26.4777ZM199.595 23.6279H204.061C204.083 23.3311 204.043 23.033 203.943 22.7527C203.843 22.4724 203.685 22.2162 203.48 22.0004C203.275 21.7847 203.027 21.6142 202.752 21.5C202.478 21.3857 202.182 21.3302 201.884 21.3371C201.583 21.3333 201.283 21.3899 201.003 21.5037C200.724 21.6175 200.47 21.7861 200.256 21.9996C200.043 22.2131 199.875 22.4672 199.761 22.7468C199.647 23.0265 199.591 23.326 199.595 23.6279Z" fill="white"/>
                          </g>
                          <defs>
                            <clipPath id="clip0_3621_9412">
                              <rect width="235" height="78.5532" fill="white"/>
                            </clipPath>
                          </defs>
                        </svg>
                    </>
                  ) : (
                    <>
                      {/* <Spacing lg="32" sm="32" /> */}
                      <h1 className="docpro_mb_0" style={{ color: "red", paddingBottom: "10px" }}>Invalid Invitation</h1>
                      <p>The invitation link you used is invalid or has expired. Please contact your administrator for a new invitation.</p>
                      <Spacing lg="20" sm="20" />
                      <Button onClick={() => navigate("/login", { replace: true })}>Back to Login</Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
